import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PATHS } from '../../routes/paths';
import DSLoading from '../../components/designSystem/DSLoading';
import { useAppDispatch } from '../../store/hooks';
import { snsSignIn } from '../../Module/Auth/Presentation/authActions';
import { SNS_STATE_STORAGE_KEY, type SnsLoginState, type SnsSignupState } from '../../Utils/snsLogin';

/**
 * 카카오 인증 콜백 (/auth/kakao/callback)
 * - code 를 sapi 에 보내 연결된 계정 목록과 티켓을 받은 뒤 로그인 화면(계정 선택)으로 넘긴다
 * - 글로벌 ID(gid)가 없고 글로벌 ID 미가입(is_new_user)이면 연결된 계정이 있어도 회원가입 화면으로 바로 넘긴다
 *   (글로벌 ID 연동이 꺼져 있을 때는 연결된 계정이 없는 경우에만)
 *   뒤로 가면 로그인 화면의 계정 선택·이메일 연결로 돌아갈 수 있게 로그인 화면을 먼저 쌓는다
 */
const SnsCallback = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation('translation');
  // 카카오 code 는 1회용 — StrictMode 의 effect 재실행으로 두 번 보내지 않게 한다
  const processedCodeRef = useRef<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code')?.trim() ?? '';
    const state = params.get('state') ?? '';
    const kakaoError = params.get('error');

    if (processedCodeRef.current === (code || 'none')) return;
    processedCodeRef.current = code || 'none';

    const backToLogin = (message?: string) => {
      if (message) window.alert(message);
      navigate(PATHS.LOGIN, { replace: true });
    };

    let savedState: string | null = null;
    try {
      savedState = window.sessionStorage.getItem(SNS_STATE_STORAGE_KEY);
      window.sessionStorage.removeItem(SNS_STATE_STORAGE_KEY);
    } catch {
      // ignore storage failures
    }

    // 사용자가 카카오 동의 화면에서 취소한 경우
    if (kakaoError) {
      backToLogin();
      return;
    }
    if (!code || !state || !savedState || state !== savedState) {
      backToLogin(t('page.login.sns.errors.callbackFailed'));
      return;
    }

    const run = async () => {
      try {
        const result = await dispatch(
          snsSignIn({
            provider: 'kakao',
            code,
            redirect_uri: `${window.location.origin}${PATHS.SNS_KAKAO_CALLBACK}`,
            partner: (process.env.REACT_APP_PARTNER as string | undefined) ?? 'OMF',
          }),
        ).unwrap();

        const sns: SnsLoginState = {
          ticket: result.ticket,
          accounts: result.accounts,
          profile: result.profile,
          gid: result.gid,
        };
        navigate(PATHS.LOGIN, { replace: true, state: { sns } });

        if (!result.gid && (result.isNewUser || result.accounts.length === 0)) {
          const snsSignup: SnsSignupState = { ticket: result.ticket, profile: result.profile };
          navigate(PATHS.SIGNUP, { state: { snsSignup } });
        }
      } catch (error) {
        console.error('SNS sign-in failed:', error);
        backToLogin(t('page.login.sns.errors.callbackFailed'));
      }
    };

    void run();
  }, [dispatch, navigate, t]);

  return (
    <Container>
      <DSLoading variant="content" size={200} marginTop="0" />
      <Message>{t('page.login.sns.processing')}</Message>
    </Container>
  );
};

const Container = styled.div`
  min-height: 100dvh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  box-sizing: border-box;
`;

const Message = styled.p`
  margin: 8px 0 0;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
`;

export default SnsCallback;
